/**
 * UI Handlers
 * Manages UI interactions like toasts, countdown timers, and button states
 */
class UIHandlers {
  constructor(options = {}) {
    this.toastContainer = options.toastContainer;
    this.generateButton = options.generateButton;
    this.buttonText = options.buttonText;
    this.loadingSpinner = options.loadingSpinner;
    this.btnLoader = options.btnLoader;
    this.input = options.input;
    this.productType = options.productType || 'default';
    
    // Gallery loading overlay reference
    this.galleryLoadingOverlay = options.galleryLoadingOverlay;
    
    // New integrated upload elements
    this.integratedDropArea = null;
    this.dropZone = null;
    this.integratedPreview = null;
    this.integratedPreviewImg = null;
    this.integratedRemove = null;
    this.imageUpload = null;
    
    this.countdown = 0;
    this.countdownInterval = null;
    this.isGenerating = false;
    this.uploadedFile = null;
    this.uploadedFileBase64 = null;
    this.isConvertingHeic = false;
    this.heic2anyLib = null;
    this.eventsSetup = false; // Flag to prevent duplicate event listeners
    
    // SIMPLIFIED: Only one processed file for both preview and upload
    this.processedFile = null;
  }

  /**
   * Show gallery loading overlay
   */
  showGalleryLoading() {
    if (this.galleryLoadingOverlay) {
      this.galleryLoadingOverlay.classList.remove('hidden');
      console.log('[UIHandlers] Gallery loading overlay shown');
    } else {
      console.warn('[UIHandlers] Gallery loading overlay element not found');
    }
  }

  /**
   * Hide gallery loading overlay
   */
  hideGalleryLoading() {
    if (this.galleryLoadingOverlay) {
      this.galleryLoadingOverlay.classList.add('hidden');
      console.log('[UIHandlers] Gallery loading overlay hidden');
    } else {
      console.warn('[UIHandlers] Gallery loading overlay element not found');
    }
  }

  /**
   * Set the product type
   */
  setProductType(productType) {
    this.productType = productType;
    console.log('[UIHandlers] Product type set to:', productType);
  }

  /**
   * Initialize integrated upload elements
   */
  initializeIntegratedUpload() {
    this.integratedDropArea = document.querySelector('[data-integrated-drop-area]');
    this.dropZone = document.querySelector('[data-drop-zone]');
    this.integratedPreview = document.querySelector('[data-integrated-preview]');
    this.integratedPreviewImg = document.querySelector('[data-integrated-preview-img]');
    this.integratedRemove = document.querySelector('[data-integrated-remove]');
    this.imageUpload = document.querySelector('[data-image-upload]');
    
    if (this.dropZone && this.imageUpload) {
      this.setupIntegratedUploadEvents();
    }
  }

  /**
   * Setup integrated upload event listeners
   */
  setupIntegratedUploadEvents() {
    // Prevent duplicate event listeners
    if (this.eventsSetup) {
      console.log('[UIHandlers] Events already setup, skipping...');
      return;
    }
    
    // Click to select file
    this.dropZone.addEventListener('click', () => {
      this.imageUpload.click();
    });
    
    // File input change
    this.imageUpload.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.handleFileUpload(file);
      }
    });
    
    // Drag and drop events
    this.dropZone.addEventListener('dragenter', this.handleDragEnter.bind(this));
    this.dropZone.addEventListener('dragover', this.handleDragOver.bind(this));
    this.dropZone.addEventListener('dragleave', this.handleDragLeave.bind(this));
    this.dropZone.addEventListener('drop', this.handleDrop.bind(this));
    
    // Remove button
    if (this.integratedRemove) {
      this.integratedRemove.addEventListener('click', () => {
        this.clearUploadedImage();
      });
    }
    
    // Mark as setup
    this.eventsSetup = true;
    console.log('[UIHandlers] Event listeners setup completed');
  }

  /**
   * Handle drag enter
   */
  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.add('dragover');
  }

  /**
   * Handle drag over
   */
  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.add('dragover');
  }

  /**
   * Handle drag leave
   */
  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    // Only remove dragover if we're leaving the drop zone entirely
    if (!this.dropZone.contains(e.relatedTarget)) {
      this.dropZone.classList.remove('dragover');
    }
  }

  /**
   * Handle drop
   */
  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.remove('dragover');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        this.handleFileUpload(file);
      } else {
        this.showToast(window.t('toasts.select_image_files_only'), 'error');
      }
    }
  }

  /**
   * Check if file is HEIC format
   */
  isHeicFile(file) {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    // Check file extension and MIME type
    const isHeicExtension = fileName.endsWith('.heic') || fileName.endsWith('.heif');
    const isHeicMimeType = fileType === 'image/heic' || fileType === 'image/heif' || 
                          fileType === 'image/x-heic' || fileType === 'image/x-heif';
    
    console.log('[UIHandlers] HEIC file check:', {
      fileName,
      fileType,
      isHeicExtension,
      isHeicMimeType,
      result: isHeicExtension || isHeicMimeType
    });
    
    return isHeicExtension || isHeicMimeType;
  }

  /**
   * Load heic2any library with improved CDN fallback
   */
  async loadHeic2Any() {
    if (this.heic2anyLib) {
      return this.heic2anyLib;
    }
    
    try {
      console.log('[UIHandlers] Loading heic2any library...');
      
      // Try different import strategies
      let module;
      
      // Strategy 1: Try dynamic import with specific path
      try {
        module = await import('/node_modules/heic2any/dist/heic2any.js');
      } catch (e1) {
        console.log('[UIHandlers] Strategy 1 failed, trying strategy 2...');
        
        // Strategy 2: Try standard dynamic import
        try {
          module = await import('heic2any');
        } catch (e2) {
          console.log('[UIHandlers] Strategy 2 failed, trying CDN fallback...');
          
          // Strategy 3: Load from CDN as fallback
          return await this.loadHeic2AnyFromCDN();
        }
      }
      
      this.heic2anyLib = module.default || module;
      console.log('[UIHandlers] heic2any library loaded successfully');
      return this.heic2anyLib;
      
    } catch (error) {
      console.error('[UIHandlers] Failed to load heic2any library:', error);
      throw new Error('Falha ao carregar biblioteca de conversão HEIC');
    }
  }

  /**
   * Load heic2any from CDN as fallback
   */
  async loadHeic2AnyFromCDN() {
    return new Promise((resolve, reject) => {
      // Check if already loaded from CDN
      if (window.heic2any) {
        console.log('[UIHandlers] heic2any already loaded from CDN');
        this.heic2anyLib = window.heic2any;
        resolve(this.heic2anyLib);
        return;
      }
      
      console.log('[UIHandlers] Loading heic2any from CDN...');
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/heic2any@latest/dist/heic2any.min.js';
      script.onload = () => {
        if (window.heic2any) {
          console.log('[UIHandlers] heic2any loaded successfully from CDN');
          this.heic2anyLib = window.heic2any;
          resolve(this.heic2anyLib);
        } else {
          reject(new Error('heic2any não carregou corretamente do CDN'));
        }
      };
      script.onerror = () => {
        reject(new Error('Falha ao carregar heic2any do CDN'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Validate HEIC file before conversion
   */
  validateHeicFile(file) {
    // Check file size (max 50MB for HEIC)
    if (file.size > 50 * 1024 * 1024) {
      throw new Error('Arquivo HEIC muito grande. Máximo 50MB.');
    }
    
    // Check if file has content
    if (file.size === 0) {
      throw new Error('Arquivo HEIC vazio ou corrompido.');
    }
    
    console.log('[UIHandlers] HEIC file validation passed:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    return true;
  }

  /**
   * Enhanced HEIC to JPEG conversion with better error handling and validation
   */
  async convertHeicToJpeg(file) {
    try {
      console.log('[UIHandlers] Starting HEIC conversion for:', file.name);
      
      // Validate the HEIC file first
      this.validateHeicFile(file);
      
      // Load the heic2any library
      const heic2any = await this.loadHeic2Any();
      
      console.log('[UIHandlers] Converting HEIC file with options:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      // Convert HEIC to JPEG with timeout
      const conversionPromise = heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9, // High quality for better results
        multiple: false // Ensure single output
      });
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Conversão HEIC expirou após 30 segundos')), 30000);
      });
      
      const result = await Promise.race([conversionPromise, timeoutPromise]);
      console.log('[UIHandlers] HEIC conversion result type:', typeof result, Array.isArray(result));
      
      // Handle different result types
      let convertedBlob;
      if (Array.isArray(result)) {
        // Some versions return an array
        convertedBlob = result[0];
        console.log('[UIHandlers] Using first blob from array result');
      } else if (result instanceof Blob) {
        // Direct blob result
        convertedBlob = result;
        console.log('[UIHandlers] Using direct blob result');
      } else {
        console.error('[UIHandlers] Unexpected conversion result:', result);
        throw new Error('Resultado de conversão HEIC inválido');
      }
      
      // Validate the converted blob
      if (!convertedBlob || !(convertedBlob instanceof Blob)) {
        console.error('[UIHandlers] Invalid converted blob:', convertedBlob);
        throw new Error('Falha na conversão: resultado inválido');
      }
      
      if (convertedBlob.size === 0) {
        console.error('[UIHandlers] Converted blob is empty');
        throw new Error('Falha na conversão: arquivo resultante vazio');
      }
      
      // Create a new File object from the converted blob
      const convertedFile = new File(
        [convertedBlob], 
        file.name.replace(/\.(heic|heif)$/i, '.jpg'),
        { 
          type: 'image/jpeg',
          lastModified: file.lastModified 
        }
      );
      
      console.log('[UIHandlers] HEIC conversion successful:', {
        originalSize: file.size,
        convertedSize: convertedFile.size,
        convertedName: convertedFile.name
      });
      
      return convertedFile;
      
    } catch (error) {
      console.error('[UIHandlers] HEIC conversion failed:', error);
      
      // Provide more specific error messages
      if (error.message.includes('timeout') || error.message.includes('expirou')) {
        throw new Error('Conversão HEIC demorou muito. Tente com um arquivo menor.');
      } else if (error.message.includes('muito grande')) {
        throw error; // Re-throw size validation errors as-is
      } else if (error.message.includes('carregar biblioteca')) {
        throw error; // Re-throw library loading errors as-is
      } else {
        throw new Error('Falha ao converter imagem HEIC. O arquivo pode estar corrompido ou em formato incompatível.');
      }
    }
  }

  /**
   * UPDATED: Handle file upload WITH EXIF orientation processing for correct upload
   */
  async handleFileUpload(file) {
    console.log('[UIHandlers] handleFileUpload called with file:', file.name);
    console.log('[UIHandlers] SIMPLIFIED MODE: Using Canvas-processed file for both preview and upload');
    
    // Basic validation
    if (!file.type.startsWith('image/') && !this.isHeicFile(file)) {
      this.showToast(window.t('toasts.select_image_files_only'), 'error');
      return;
    }
    
    // File size check (50MB limit for all images)
    const maxSize = 50 * 1024 * 1024; // 50MB for all image types
    if (file.size > maxSize) {
      this.showToast(window.t('toasts.file_too_large_general'), 'error');
      return;
    }
    
    // Clear previous state first
    this.clearUploadedImage();
    
    let fileToProcess = file;
    
    // Convert HEIC files to JPEG first
    if (this.isHeicFile(file)) {
      try {
        this.isConvertingHeic = true;
        this.showToast(window.t('toasts.converting_heic'), 'info');
        this.updateButtonStatus();
        
        fileToProcess = await this.convertHeicToJpeg(file);
        this.showToast(window.t('toasts.heic_converted_success'), 'success');
        
      } catch (error) {
        console.error('[UIHandlers] HEIC conversion error:', error);
        this.showToast(error.message, 'error');
        this.isConvertingHeic = false;
        this.updateButtonStatus();
        return;
      } finally {
        this.isConvertingHeic = false;
      }
    }
    
    // Process EXIF orientation to create upload-ready file
    try {
      console.log('[UIHandlers] Processing EXIF orientation...');
      
      const orientationResult = await window.ImageExifUtils.processImageOrientation(fileToProcess);
      
      // Store the processed file for both preview and upload
      this.processedFile = orientationResult.uploadFile; // Canvas-processed file
      this.uploadedFile = this.processedFile; // For compatibility
      
      console.log('[UIHandlers] EXIF processing completed:', {
        processedFile: this.processedFile.name,
        processedSize: this.processedFile.size
      });
      
    } catch (error) {
      console.error('[UIHandlers] EXIF processing error:', error);
      // Fallback: use original file
      this.processedFile = fileToProcess;
      this.uploadedFile = fileToProcess;
      console.log('[UIHandlers] Using original file as fallback');
    }
    
    // Create preview and base64 from the SAME processed file
    const reader = new FileReader();
    reader.onload = (e) => {
      console.log('[UIHandlers] FileReader onload - using PROCESSED file for preview');
      
      // Store the base64 data from the processed file
      this.uploadedFileBase64 = e.target.result;
      console.log('[UIHandlers] Base64 created from processed file:', {
        dataLength: this.uploadedFileBase64.length,
        fileSize: this.processedFile.size
      });
      
      // Show preview using processed file
      if (this.integratedPreviewImg) {
        const timestamp = Date.now();
        this.integratedPreviewImg.src = e.target.result; // Processed file
        this.integratedPreviewImg.setAttribute('data-timestamp', timestamp);
        console.log('[UIHandlers] Image preview updated with PROCESSED file:', timestamp);
        this.showImagePreview();
      }
      
      // Update button status after image is loaded
      this.updateButtonStatus();
    };
    
    reader.onerror = (error) => {
      console.error('[UIHandlers] FileReader error:', error);
      this.showToast(window.t('toasts.image_process_error_retry'), 'error');
      this.clearUploadedImage();
    };
    
    reader.readAsDataURL(this.processedFile); // Use processed file for both preview and base64
    
    if (!this.isHeicFile(file)) {
      this.showToast(window.t('toasts.image_loaded_processed_success'), 'success');
    }
  }

  /**
   * Enhanced show image preview with better error handling and reinitialization
   */
  showImagePreview() {
    console.log('[UIHandlers] showImagePreview called');
    console.log('[UIHandlers] Elements check:', {
      dropZone: !!this.dropZone,
      integratedPreview: !!this.integratedPreview,
      integratedPreviewImg: !!this.integratedPreviewImg
    });
    
    if (!this.dropZone || !this.integratedPreview) {
      console.warn('[UIHandlers] Missing required elements for preview, attempting reinitialization...');
      this.initializeIntegratedUpload();
      
      // Try again after reinitialization
      if (!this.dropZone || !this.integratedPreview) {
        console.error('[UIHandlers] Failed to initialize upload elements');
        return;
      }
    }
    
    try {
      this.dropZone.style.display = 'none';
      this.integratedPreview.classList.remove('hidden');
      console.log('[UIHandlers] ✅ Preview shown successfully, drop zone hidden');
    } catch (error) {
      console.error('[UIHandlers] Error showing image preview:', error);
    }
  }

  /**
   * Enhanced clear uploaded image with thorough cleanup
   */
  clearUploadedImage() {
    console.log('[UIHandlers] clearUploadedImage called');
    
    // Clear all file references
    this.uploadedFile = null;
    this.processedFile = null;
    this.uploadedFileBase64 = null;
    console.log('[UIHandlers] All file data cleared');
    
    // Clear file input
    if (this.imageUpload) {
      this.imageUpload.value = '';
      console.log('[UIHandlers] File input cleared');
    }
    
    // Clear preview image src first
    if (this.integratedPreviewImg) {
      this.integratedPreviewImg.src = '';
      this.integratedPreviewImg.removeAttribute('data-timestamp');
      console.log('[UIHandlers] Preview image src cleared');
    }
    
    // Hide preview and show drop zone
    if (this.integratedPreview && this.dropZone) {
      this.integratedPreview.classList.add('hidden');
      this.dropZone.style.display = 'flex';
      console.log('[UIHandlers] Preview hidden, drop zone shown');
    }
    
    // Update button status after clearing image
    this.updateButtonStatus();
  }

  /**
   * Get uploaded file with validation (returns processed file)
   */
  getUploadedFile() {
    return this.processedFile || this.uploadedFile;
  }

  /**
   * SIMPLIFIED: Get upload-ready file (same as uploaded file now)
   */
  getUploadReadyFile() {
    console.log('[UIHandlers] getUploadReadyFile called');
    const file = this.processedFile || this.uploadedFile;
    
    if (file) {
      console.log('[UIHandlers] Returning processed file:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    } else {
      console.log('[UIHandlers] No file available for upload');
    }
    
    return file;
  }

  /**
   * SIMPLIFIED: Get uploaded file base64 data (from processed file)
   */
  getUploadedFileBase64() {
    console.log('[UIHandlers] getUploadedFileBase64 called');
    
    if (this.uploadedFileBase64) {
      console.log('[UIHandlers] Returning base64 data from processed file');
      console.log('[UIHandlers] Base64 data length:', this.uploadedFileBase64.length, 'characters');
      return this.uploadedFileBase64;
    }
    
    console.log('[UIHandlers] No base64 data available');
    return null;
  }

  /**
   * Check if user has uploaded an image
   */
  hasUploadedImage() {
    return !!((this.processedFile || this.uploadedFile) && this.uploadedFileBase64);
  }

  /**
   * SIMPLIFIED: Get uploaded image data for localStorage (using processed file)
   */
  getUploadedImageDataForStorage() {
    console.log('[UIHandlers] getUploadedImageDataForStorage called');
    
    if (this.hasUploadedImage()) {
      const file = this.processedFile || this.uploadedFile;
      
      console.log('[UIHandlers] Preparing processed file data for storage:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      return {
        base64: this.uploadedFileBase64,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      };
    }
    
    console.log('[UIHandlers] No image data available for storage');
    return null;
  }

  /**
   * Show/hide integrated drop area
   */
  showIntegratedDropArea() {
    if (this.integratedDropArea) {
      this.integratedDropArea.classList.remove('hidden');
    }
  }

  hideIntegratedDropArea() {
    if (this.integratedDropArea) {
      this.integratedDropArea.classList.add('hidden');
    }
  }

  /**
   * Set the toast container element
   * @param {HTMLElement} container 
   */
  setToastContainer(container) {
    this.toastContainer = container;
  }

  /**
   * Set the generate button element
   * @param {HTMLElement} button 
   */
  setGenerateButton(button) {
    this.generateButton = button;
  }

  /**
   * Set the button text element
   * @param {HTMLElement} element 
   */
  setButtonText(element) {
    this.buttonText = element;
  }

  /**
   * Set the loading spinner element
   * @param {HTMLElement} spinner 
   */
  setLoadingSpinner(spinner) {
    this.loadingSpinner = spinner;
  }

  /**
   * Set the button loader element
   * @param {HTMLElement} loader 
   */
  setButtonLoader(loader) {
    this.btnLoader = loader;
  }

  /**
   * Set the input element
   * @param {HTMLElement} input 
   */
  setInput(input) {
    this.input = input;
  }

  /**
   * Show a toast notification
   * @param {string} message Message to display
   * @param {string} type Type of toast (info, success, error)
   */
  showToast(message, type = 'info') {
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.className = 'toast-container';
      this.toastContainer.setAttribute('data-toast', '');
      document.body.appendChild(this.toastContainer);
    }
    
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.textContent = message;
    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('toast--fade-out');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Enhanced update button status to consider product type, uploaded image, and HEIC conversion
   */
  updateButtonStatus() {
    if (!this.generateButton) return;
    
    console.log('[UIHandlers] updateButtonStatus called for product type:', this.productType);
    
    // Check if input has content
    const hasInputContent = this.input && this.input.value.trim();
    console.log('[UIHandlers] Has input content:', hasInputContent);
    
    // For image_card_with_upload, also check if there's an uploaded image
    let isValid = false;
    if (this.productType === 'image_card_with_upload') {
      const hasImage = this.hasUploadedImage();
      console.log('[UIHandlers] Has uploaded image:', hasImage);
      isValid = hasInputContent && hasImage && !this.isConvertingHeic;
      console.log('[UIHandlers] image_card_with_upload validation - input:', hasInputContent, 'image:', hasImage, 'converting:', this.isConvertingHeic, 'valid:', isValid);
    } else {
      // For other product types, only check input content
      isValid = hasInputContent;
      console.log('[UIHandlers] Other product type validation - valid:', isValid);
    }
    
    // Button is disabled if invalid, generating, in cooldown, or converting HEIC
    this.generateButton.disabled = !isValid || this.isGenerating || this.countdown > 0 || this.isConvertingHeic;
    
    // Add/remove has-content class based on validation
    if (isValid && !this.isConvertingHeic) {
      this.generateButton.classList.add('has-content');
    } else {
      this.generateButton.classList.remove('has-content');
    }
    
    console.log('[UIHandlers] Button disabled:', this.generateButton.disabled, 'has-content:', isValid && !this.isConvertingHeic);
  }

  /**
   * Set the generating state
   * @param {boolean} isGenerating 
   */
  setGenerating(isGenerating) {
    this.isGenerating = isGenerating;
    
    // Notify main form
    if (isGenerating) {
      document.dispatchEvent(new Event('imageGenerationStarted'));
      localStorage.setItem('lastGenerationAttempt', Date.now().toString());
    } else {
      document.dispatchEvent(new Event('imageGenerationEnded'));
      localStorage.removeItem('lastGenerationAttempt');
    }
    
    this.updateButtonText();
    
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.toggle('hidden', !isGenerating);
      this.loadingSpinner.style.opacity = isGenerating ? '1' : '0';
    }
    
    if (this.btnLoader) {
      // Toggle the visibility of the circular dot loader
      this.btnLoader.classList.toggle('hidden', !isGenerating);
    }
    
    // IMPORTANT: Keep the has-content class if input has content (regardless of disabled state)
    if (this.input && this.input.value.trim() && this.generateButton) {
      this.generateButton.classList.add('has-content');
    }
    
    this.updateButtonStatus();
  }

  /**
   * Force reset the state (used for recovery from errors)
   */
  forceResetState() {
    console.log('Forcing state reset');
    this.isGenerating = false;
    this.countdown = 0;
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    localStorage.removeItem('lastGenerationAttempt');
    this.updateButtonStatus();
    document.dispatchEvent(new Event('imageGenerationEnded'));
  }

  /**
   * Start the countdown timer
   * @param {number} seconds Duration in seconds 
   */
  startCountdown(seconds = 5) {
    this.countdown = seconds;
    this.updateButtonText();
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      this.updateButtonText();
      
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.updateButtonStatus();
      }
    }, 1000);
    
    localStorage.setItem('lastImageGeneration', Date.now().toString());
  }

  /**
   * Start countdown if needed (e.g. on page load)
   */
  startCountdownIfNeeded() {
    const lastGeneration = localStorage.getItem('lastImageGeneration');
    if (lastGeneration) {
      const timeSinceLastGeneration = (Date.now() - parseInt(lastGeneration)) / 1000;
      if (timeSinceLastGeneration < 5) {
        this.countdown = Math.ceil(5 - timeSinceLastGeneration);
        this.startCountdown(this.countdown);
      }
    }
  }

  /**
   * Update the button text based on current state
   */
  updateButtonText() {
    if (!this.buttonText) {
      console.warn('Button text element not found');
      return;
    }
    
    if (this.isGenerating) {
      // Show the loading spinner and hide the button text
      if (this.loadingSpinner) {
        this.loadingSpinner.classList.remove('hidden');
        this.loadingSpinner.style.opacity = '1';
      }
      
      // Show circular dot loader
      if (this.btnLoader) {
        this.btnLoader.classList.remove('hidden');
      }
      
      // Clear button text when loading
      this.buttonText.innerHTML = '';
      
      // Keep has-content class based on validation logic
      this.updateButtonStatus();
    } else if (this.countdown > 0) {
      // In countdown state
      if (this.loadingSpinner) {
        this.loadingSpinner.classList.add('hidden');
        this.loadingSpinner.style.opacity = '0';
      }
      
      // Hide circular dot loader during countdown
      if (this.btnLoader) {
        this.btnLoader.classList.add('hidden');
      }
      
      // Show countdown text
      this.buttonText.innerHTML = `<span class="countdown-timer">${this.countdown}s</span>`;
    } else {
      // Normal state - show the arrow icon
      if (this.loadingSpinner) {
        this.loadingSpinner.classList.add('hidden');
        this.loadingSpinner.style.opacity = '0';
      }
      
      // Hide circular dot loader in normal state
      if (this.btnLoader) {
        this.btnLoader.classList.add('hidden');
      }
      
      // Show arrow icon
      this.buttonText.innerHTML = `
        <svg class="letz-ai-imggen-btn-icon" viewBox="0 0 16 16" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" d="M2 8.00012H14M14 8.00012L9.33333 3.3335M14 8.00012L9.33333 12.6668"></path>
        </svg>
      `;
    }
    
    // Update button status with the enhanced validation logic
    this.updateButtonStatus();
  }

  /**
   * Check if cool down is active
   * @returns {boolean}
   */
  isCooldownActive() {
    return this.countdown > 0;
  }

  /**
   * Check if generation is in progress
   * @returns {boolean}
   */
  isGenerationInProgress() {
    return this.isGenerating;
  }

  /**
   * Load an image from URL and process it as if it was uploaded
   * @param {string} imageUrl - The URL of the image to load
   * @param {string} filename - The filename to use for the downloaded image
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  async loadImageFromUrl(imageUrl, filename = 'imagem-url.jpg') {
    try {
      console.log('[UIHandlers] Loading image from URL:', imageUrl);
      
      // Show loading state
      this.showToast(window.t('toasts.loading_image'), 'info');
      
      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Convert response to blob
      const blob = await response.blob();
      
      // Create a File object from the blob
      const file = new File([blob], filename, { 
        type: blob.type || 'image/jpeg',
        lastModified: Date.now()
      });
      
      console.log('[UIHandlers] Image downloaded successfully:', {
        filename: file.name,
        size: file.size,
        type: file.type
      });
      
      // Convert to base64 synchronously like localStorage method
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = (e) => {
          const base64 = e.target.result;
          
          // Set the uploaded file and base64 data (like localStorage method)
          this.uploadedFile = file;
          this.uploadedFileBase64 = base64;
          
          console.log('[UIHandlers] Base64 ready, updating preview image...');
          
          // Ensure elements are initialized
          if (!this.integratedPreviewImg) {
            this.initializeIntegratedUpload();
          }
          
          // Update preview image src directly (like localStorage method)
          if (this.integratedPreviewImg) {
            this.integratedPreviewImg.src = base64;
            console.log('[UIHandlers] Preview image src updated');
          }
          
          // Now show the preview (synchronized like localStorage)
          this.showImagePreview();
          
          console.log('[UIHandlers] ✅ Image loaded from URL and preview shown');
          resolve(true);
        };
        
        reader.onerror = () => {
          console.error('[UIHandlers] Error reading file as base64');
          this.showToast(window.t('toasts.image_process_error'), 'error');
          resolve(false);
        };
        
        reader.readAsDataURL(file);
      });
      
    } catch (error) {
      console.error('[UIHandlers] Error loading image from URL:', error);
      this.showToast(window.t('toasts.url_image_load_error'), 'error');
      return false;
    }
  }
}

// Export the class
window.UIHandlers = UIHandlers;
